import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { StatCard } from "@/components/StatCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  ToggleGroup, ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis, ComposedChart, Line,
} from "recharts";
import {
  analyticsAnnualSummary, analyticsByCategoryDeep, analyticsByVendor,
  analyticsCategoryTrend, analyticsMatrix, analyticsRecurringVsOnetime,
  analyticsVendorTrend, listCategories, listVendors,
} from "@/lib/api";
import { EUR, fmtMonth, monthBounds, num, paymentMethodOptions, recurrenceIT } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import { PaymentMethodBreakdown } from "@/components/PaymentMethodBreakdown";

const BAR_COLORS = ["#2A4B3C", "#B45309", "#A16207", "#0F766E", "#7C5E3C", "#9F1239", "#475569"];

export default function Analytics() {
  const thisYear = new Date().getFullYear();
  const [basis, setBasis] = useState("accrual");
  const [dateFrom, setDateFrom] = useState(`${thisYear}-01-01`);
  const [dateTo, setDateTo] = useState(`${thisYear}-12-31`);
  const [year, setYear] = useState(thisYear);
  const [method, setMethod] = useState("");

  return (
    <div data-testid="analytics-page">
      <PageHeader
        title="Analisi interna"
        subtitle="Reporting avanzato per il controllo di gestione — solo per uso interno"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker
              value={{ from: dateFrom, to: dateTo }}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
              testId="an-period"
              align="end"
            />
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Dal</Label>
              <Input
                type="date"
                data-testid="analytics-date-from"
                className="w-[150px]"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Al</Label>
              <Input
                type="date"
                data-testid="analytics-date-to"
                className="w-[150px]"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Anno</Label>
              <Input
                type="number"
                data-testid="analytics-year"
                className="w-[90px]"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
            <ToggleGroup
              type="single"
              value={basis}
              onValueChange={(v) => v && setBasis(v)}
            >
              <ToggleGroupItem value="cash" data-testid="analytics-basis-cash">
                Cassa
              </ToggleGroupItem>
              <ToggleGroupItem value="accrual" data-testid="analytics-basis-accrual">
                Competenza
              </ToggleGroupItem>
            </ToggleGroup>
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">Metodo</Label>
              <Select value={method || "all"} onValueChange={(v) => setMethod(v === "all" ? "" : v)}>
                <SelectTrigger
                  data-testid="analytics-method"
                  className="h-9 w-[130px]"
                >
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {paymentMethodOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        }
      />

      <Tabs defaultValue="summary">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="summary" data-testid="tab-summary">Riepilogo annuale</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Fornitori</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categorie</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Incrociata</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">
            Ricorrenti vs Una tantum
          </TabsTrigger>
          <TabsTrigger value="methods" data-testid="tab-methods">
            Metodi di pagamento
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-5">
          <AnnualSummary year={year} basis={basis} method={method} />
        </TabsContent>
        <TabsContent value="vendors" className="mt-5">
          <VendorAnalysis
            dateFrom={dateFrom}
            dateTo={dateTo}
            basis={basis}
            year={year}
            method={method}
          />
        </TabsContent>
        <TabsContent value="categories" className="mt-5">
          <CategoryAnalysis
            dateFrom={dateFrom}
            dateTo={dateTo}
            basis={basis}
            year={year}
            method={method}
          />
        </TabsContent>
        <TabsContent value="matrix" className="mt-5">
          <MatrixView dateFrom={dateFrom} dateTo={dateTo} basis={basis} method={method} />
        </TabsContent>
        <TabsContent value="recurring" className="mt-5">
          <RecurringView dateFrom={dateFrom} dateTo={dateTo} basis={basis} method={method} />
        </TabsContent>
        <TabsContent value="methods" className="mt-5">
          <MethodsAnalysis dateFrom={dateFrom} dateTo={dateTo} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Annual summary ---------- */
function AnnualSummary({ year, basis, method }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const params = { year, basis };
      if (method) params.method = method;
      const s = await analyticsAnnualSummary(params);
      setData(s);
    })();
  }, [year, basis, method]);

  if (!data) return <p className="text-sm text-stone-500">Caricamento…</p>;

  const monthly = (data.monthly || []).map((r) => ({
    month: fmtMonth(data.year, r.month),
    planned: num(r.planned),
    actual: num(r.actual),
  }));

  return (
    <section data-testid="section-summary" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">
          Riepilogo {year} — base {basis === "cash" ? "cassa" : "competenza"}
        </div>
        <ExportButtons
          sheets={[
            {
              name: "Riepilogo",
              rows: [
                {
                  metric: `Pianificato ${year}`,
                  value: num(data.total_planned),
                },
                {
                  metric: `Pagato ${year} (${basis})`,
                  value: num(data.total_actual),
                },
                { metric: "Scostamento", value: num(data.variance) },
                { metric: "Ancora da pagare", value: num(data.still_to_pay) },
              ],
              columns: [
                { key: "metric", header: "Metrica" },
                { key: "value", header: "Importo (€)", type: "currency" },
              ],
            },
            {
              name: "Mensile",
              rows: monthly,
              columns: [
                { key: "month", header: "Mese" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
              ],
            },
            {
              name: "Top fornitori",
              rows: data.top_vendors || [],
              columns: [
                { key: "vendor_name", header: "Fornitore" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "variance", header: "Δ (€)", type: "currency" },
              ],
            },
            {
              name: "Top categorie",
              rows: data.top_categories || [],
              columns: [
                { key: "category_name", header: "Categoria" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "variance", header: "Δ (€)", type: "currency" },
              ],
            },
          ]}
          filenameBase={buildFilename(`riepilogo_annuale_${basis}`, { year })}
          testId="export-summary"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard testId="summary-planned" label={`Pianificato ${year}`} value={num(data.total_planned)} tone="info" />
        <StatCard testId="summary-actual" label={`Pagato ${year}`} value={num(data.total_actual)} tone="positive" />
        <StatCard
          testId="summary-variance"
          label="Scostamento"
          value={num(data.variance)}
          tone={num(data.variance) > 0 ? "warning" : num(data.variance) < 0 ? "negative" : "default"}
        />
        <StatCard testId="summary-pending" label="Ancora da pagare" value={num(data.still_to_pay)} tone="warning" />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Andamento mensile ({basis === "cash" ? "cassa" : "competenza"})
        </h3>
        <div data-testid="summary-chart" className="h-72">
          <ResponsiveContainer width="100%" height="100%" minHeight={260}>
            <BarChart data={monthly} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="month" fontSize={11} stroke="#78716C" />
              <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="planned" name="Pianificato" fill="#2A4B3C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" name="Pagato" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Top 5 fornitori per costo
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fornitore</TableHead>
                <TableHead className="text-right">Pianificato</TableHead>
                <TableHead className="text-right">Pagato</TableHead>
                <TableHead className="text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_vendors.map((v) => (
                <TableRow key={v.vendor_id || v.vendor_name} data-testid={`summary-top-vendor-${v.vendor_id || "none"}`}>
                  <TableCell>{v.vendor_name}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(v.planned)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(v.actual)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(v.variance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Top 5 categorie per costo
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Pianificato</TableHead>
                <TableHead className="text-right">Pagato</TableHead>
                <TableHead className="text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_categories.map((c) => (
                <TableRow key={c.category_id} data-testid={`summary-top-category-${c.category_id}`}>
                  <TableCell>{c.category_name}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(c.planned)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(c.actual)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(c.variance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </section>
  );
}

/* ---------- Vendor analysis with drill-down trend ---------- */
function VendorAnalysis({ dateFrom, dateTo, basis, year, method }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo, basis };
      if (method) params.method = method;
      const r = await analyticsByVendor(params);
      setRows(r || []);
      const first = (r || []).find((x) => x.vendor_id) || null;
      setSelected(first?.vendor_id || null);
    })();
  }, [dateFrom, dateTo, basis, method]);

  useEffect(() => {
    if (!selected) { setTrend([]); return; }
    (async () => {
      const params = { vendor_id: selected, year, basis };
      if (method) params.method = method;
      const t = await analyticsVendorTrend(params);
      setTrend(t || []);
    })();
  }, [selected, year, basis, method]);

  const chartRows = rows.slice(0, 7).map((r, i) => ({
    name: r.vendor_name,
    actual: num(r.actual),
    planned: num(r.planned),
    color: BAR_COLORS[i % BAR_COLORS.length],
  }));

  const trendData = trend.map((r) => ({
    month: fmtMonth(r.year, r.month),
    planned: num(r.planned),
    actual: num(r.actual),
  }));

  return (
    <section data-testid="section-vendors" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">
          Analisi fornitori — {dateFrom} → {dateTo} · base {basis === "cash" ? "cassa" : "competenza"}
        </div>
        <ExportButtons
          sheets={[
            {
              name: "Per fornitore",
              rows: rows.map((r) => ({
                vendor_name: r.vendor_name,
                occurrences: r.occurrences,
                payments: r.payments,
                planned: r.planned,
                actual: r.actual,
                variance: r.variance,
                variance_pct: r.variance_pct,
              })),
              columns: [
                { key: "vendor_name", header: "Fornitore" },
                { key: "occurrences", header: "Scadenze", type: "integer" },
                { key: "payments", header: "Pagamenti", type: "integer" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "variance", header: "Δ (€)", type: "currency" },
                { key: "variance_pct", header: "Δ %", type: "percent" },
              ],
            },
            ...(selected
              ? [
                  {
                    name: `Trend ${year}`,
                    rows: trendData,
                    columns: [
                      { key: "month", header: "Mese" },
                      {
                        key: "planned",
                        header: "Pianificato (€)",
                        type: "currency",
                      },
                      { key: "actual", header: "Pagato (€)", type: "currency" },
                    ],
                  },
                ]
              : []),
          ]}
          filenameBase={buildFilename(`analisi_fornitori_${basis}`, {
            date_from: dateFrom,
            date_to: dateTo,
          })}
          testId="export-vendors"
        />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Top 7 fornitori per importo pagato
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%" minHeight={260}>
            <BarChart data={chartRows} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis type="number" fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
              <YAxis type="category" dataKey="name" fontSize={11} stroke="#78716C" width={140} />
              <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="actual" name="Pagato" radius={[0, 4, 4, 0]}>
                {chartRows.map((row, i) => (
                  <Cell key={i} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Dettaglio per fornitore
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fornitore</TableHead>
              <TableHead className="text-right">Scadenze</TableHead>
              <TableHead className="text-right">Pagamenti</TableHead>
              <TableHead className="text-right">Pianificato</TableHead>
              <TableHead className="text-right">Pagato</TableHead>
              <TableHead className="text-right">Δ</TableHead>
              <TableHead className="text-right">%</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-sm text-stone-500">
                  Nessun dato nel periodo.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow
                key={r.vendor_id || "none"}
                data-testid={`vendor-row-${r.vendor_id || "none"}`}
                className={selected === r.vendor_id ? "bg-stone-50" : ""}
              >
                <TableCell className="font-medium">{r.vendor_name}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{r.occurrences}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{r.payments}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{EUR(r.planned)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{EUR(r.actual)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{EUR(r.variance)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums text-stone-500">
                  {r.variance_pct !== null ? `${(r.variance_pct * 100).toFixed(1)}%` : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {r.vendor_id && (
                    <button
                      data-testid={`btn-trend-${r.vendor_id}`}
                      className="text-xs text-blue-700 hover:underline"
                      onClick={() => setSelected(r.vendor_id)}
                    >
                      Vedi trend
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selected && (
        <div className="rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Trend {year} — {rows.find((r) => r.vendor_id === selected)?.vendor_name}
          </h3>
          <div data-testid="vendor-trend-chart" className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <ComposedChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="month" fontSize={11} stroke="#78716C" />
                <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
                <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="planned" name="Pianificato" fill="#2A4B3C" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="actual" name="Pagato" stroke="#10B981" strokeWidth={2} dot />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Category analysis with drill-down trend ---------- */
function CategoryAnalysis({ dateFrom, dateTo, basis, year, method }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo, basis };
      if (method) params.method = method;
      const r = await analyticsByCategoryDeep(params);
      setRows(r || []);
      setSelected((r || [])[0]?.category_id || null);
    })();
  }, [dateFrom, dateTo, basis, method]);

  useEffect(() => {
    if (!selected) { setTrend([]); return; }
    (async () => {
      const params = { category_id: selected, year, basis };
      if (method) params.method = method;
      const t = await analyticsCategoryTrend(params);
      setTrend(t || []);
    })();
  }, [selected, year, basis, method]);

  const total = rows.reduce((s, r) => s + num(r.actual), 0);
  const trendData = trend.map((r) => ({
    month: fmtMonth(r.year, r.month),
    planned: num(r.planned),
    actual: num(r.actual),
  }));

  return (
    <section data-testid="section-categories" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">
          Analisi categorie — {dateFrom} → {dateTo} · base {basis === "cash" ? "cassa" : "competenza"}
        </div>
        <ExportButtons
          sheets={[
            {
              name: "Per categoria",
              rows: rows.map((r) => ({
                category_name: r.category_name,
                occurrences: r.occurrences,
                planned: r.planned,
                actual: r.actual,
                variance: r.variance,
                share_pct: total > 0 ? num(r.actual) / total : 0,
              })),
              columns: [
                { key: "category_name", header: "Categoria" },
                { key: "occurrences", header: "Scadenze", type: "integer" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "variance", header: "Δ (€)", type: "currency" },
                { key: "share_pct", header: "Quota %", type: "percent" },
              ],
            },
            ...(selected
              ? [
                  {
                    name: `Trend ${year}`,
                    rows: trendData,
                    columns: [
                      { key: "month", header: "Mese" },
                      {
                        key: "planned",
                        header: "Pianificato (€)",
                        type: "currency",
                      },
                      { key: "actual", header: "Pagato (€)", type: "currency" },
                    ],
                  },
                ]
              : []),
          ]}
          filenameBase={buildFilename(`analisi_categorie_${basis}`, {
            date_from: dateFrom,
            date_to: dateTo,
          })}
          testId="export-categories"
        />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Dettaglio per categoria
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-right">Scadenze</TableHead>
              <TableHead className="text-right">Pianificato</TableHead>
              <TableHead className="text-right">Pagato</TableHead>
              <TableHead className="text-right">Δ</TableHead>
              <TableHead className="text-right">Quota %</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-stone-500">
                  Nessun dato nel periodo.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const share = total > 0 ? (num(r.actual) / total) * 100 : 0;
              return (
                <TableRow
                  key={r.category_id}
                  data-testid={`category-row-${r.category_id}`}
                  className={selected === r.category_id ? "bg-stone-50" : ""}
                >
                  <TableCell className="flex items-center gap-2 font-medium">
                    <span
                      className="inline-block h-3 w-3 rounded-full border border-stone-200"
                      style={{ background: r.color || "#E7E5E4" }}
                    />
                    {r.category_name}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{r.occurrences}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(r.planned)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(r.actual)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(r.variance)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-stone-500">
                    {share.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`btn-cat-trend-${r.category_id}`}
                      className="text-xs text-blue-700 hover:underline"
                      onClick={() => setSelected(r.category_id)}
                    >
                      Vedi trend
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {selected && (
        <div className="rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Trend {year} — {rows.find((r) => r.category_id === selected)?.category_name}
          </h3>
          <div data-testid="category-trend-chart" className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <ComposedChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="month" fontSize={11} stroke="#78716C" />
                <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
                <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="planned" name="Pianificato" fill="#2A4B3C" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="actual" name="Pagato" stroke="#B45309" strokeWidth={2} dot />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Vendor × Category matrix ---------- */
function MatrixView({ dateFrom, dateTo, basis, method }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo, basis };
      if (method) params.method = method;
      const d = await analyticsMatrix(params);
      setData(d);
    })();
  }, [dateFrom, dateTo, basis, method]);

  if (!data) return <p className="text-sm text-stone-500">Caricamento…</p>;
  if ((data.rows || []).length === 0) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-10 text-center">
        <p className="text-sm text-stone-500">
          Nessun pagamento nel periodo selezionato.
        </p>
      </div>
    );
  }

  // Heatmap shading based on cell value relative to max
  const allVals = data.rows.flatMap((r) => Object.values(r.cells).map(num));
  const maxVal = Math.max(...allVals, 1);
  const shade = (v) => {
    const n = num(v);
    if (n <= 0) return "bg-white";
    const ratio = n / maxVal;
    if (ratio > 0.66) return "bg-amber-200/80";
    if (ratio > 0.33) return "bg-amber-100";
    return "bg-amber-50";
  };

  return (
    <section data-testid="section-matrix" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">
          Matrice categoria × fornitore — base {basis === "cash" ? "cassa" : "competenza"}
        </div>
        <ExportButtons
          rows={[
            ...data.rows.map((row) => {
              const obj = { category_name: row.category_name };
              data.vendors.forEach((v) => {
                obj[`v_${v.vendor_id || v.vendor_name}`] =
                  num(row.cells[v.vendor_id || v.vendor_name] ?? 0);
              });
              obj.row_total = num(row.row_total);
              return obj;
            }),
            (() => {
              const totRow = { category_name: "TOTALE COLONNA" };
              data.vendors.forEach((v) => {
                totRow[`v_${v.vendor_id || v.vendor_name}`] = num(v.total);
              });
              totRow.row_total = num(data.grand_total);
              return totRow;
            })(),
          ]}
          columns={[
            { key: "category_name", header: "Categoria" },
            ...data.vendors.map((v) => ({
              key: `v_${v.vendor_id || v.vendor_name}`,
              header: v.vendor_name,
              type: "currency",
            })),
            { key: "row_total", header: "Totale riga (€)", type: "currency" },
          ]}
          filenameBase={buildFilename(`matrice_categoria_fornitore_${basis}`, {
            date_from: dateFrom,
            date_to: dateTo,
          })}
          sheetName="Matrice"
          testId="export-matrix"
        />
      </div>
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900">
        <strong>Matrice categoria × fornitore.</strong> Ogni cella è l'importo
        pagato (base {basis === "cash" ? "cassa" : "competenza"}) per la
        combinazione. Le intensità di colore rendono immediatamente visibili le
        concentrazioni di spesa.
      </div>
      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="border-b border-stone-200 bg-stone-50">
            <tr>
              <th className="sticky left-0 bg-stone-50 px-4 py-2 text-left font-medium uppercase tracking-wider text-stone-600">
                Categoria ↓ / Fornitore →
              </th>
              {data.vendors.map((v) => (
                <th
                  key={v.vendor_id || v.vendor_name}
                  className="px-4 py-2 text-right font-medium uppercase tracking-wider text-stone-600"
                >
                  {v.vendor_name}
                </th>
              ))}
              <th className="border-l border-stone-200 px-4 py-2 text-right font-semibold uppercase text-stone-900">
                Totale riga
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr
                key={row.category_id}
                data-testid={`matrix-row-${row.category_id}`}
                className="border-b border-stone-100"
              >
                <td className="sticky left-0 bg-white px-4 py-2 font-medium text-stone-900">
                  {row.category_name}
                </td>
                {data.vendors.map((v) => {
                  const cell = row.cells[v.vendor_id || v.vendor_name] ?? 0;
                  return (
                    <td
                      key={v.vendor_id || v.vendor_name}
                      className={`px-4 py-2 text-right font-mono tabular-nums ${shade(cell)}`}
                    >
                      {num(cell) > 0 ? EUR(cell) : ""}
                    </td>
                  );
                })}
                <td className="border-l border-stone-200 bg-stone-50 px-4 py-2 text-right font-mono font-semibold tabular-nums">
                  {EUR(row.row_total)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-stone-300 bg-stone-50 font-semibold">
              <td className="sticky left-0 bg-stone-50 px-4 py-2">Totale colonna</td>
              {data.vendors.map((v) => (
                <td
                  key={v.vendor_id || v.vendor_name}
                  className="px-4 py-2 text-right font-mono tabular-nums"
                >
                  {EUR(v.total)}
                </td>
              ))}
              <td className="border-l border-stone-200 bg-stone-100 px-4 py-2 text-right font-mono tabular-nums">
                {EUR(data.grand_total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------- Recurring vs one-time ---------- */
function RecurringView({ dateFrom, dateTo, basis, method }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo, basis };
      if (method) params.method = method;
      const d = await analyticsRecurringVsOnetime(params);
      setData(d);
    })();
  }, [dateFrom, dateTo, basis, method]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return (data.by_type || []).map((r) => ({
      type: recurrenceIT[r.recurrence_type],
      planned: num(r.planned),
      actual: num(r.actual),
      occurrences: r.occurrences,
    }));
  }, [data]);

  if (!data) return <p className="text-sm text-stone-500">Caricamento…</p>;

  const recShare =
    num(data.recurring.actual) + num(data.one_time.actual) > 0
      ? (num(data.recurring.actual) /
          (num(data.recurring.actual) + num(data.one_time.actual))) *
        100
      : 0;

  return (
    <section data-testid="section-recurring" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wider text-stone-500">
          Ricorrenti vs una tantum — {dateFrom} → {dateTo} · base{" "}
          {basis === "cash" ? "cassa" : "competenza"}
        </div>
        <ExportButtons
          sheets={[
            {
              name: "Riepilogo",
              rows: [
                {
                  group: "Ricorrenti",
                  planned: data.recurring.planned,
                  actual: data.recurring.actual,
                },
                {
                  group: "Una tantum",
                  planned: data.one_time.planned,
                  actual: data.one_time.actual,
                },
                {
                  group: "Quota ricorrenti su totale pagato",
                  planned: null,
                  actual: null,
                  share_pct: recShare / 100,
                },
              ],
              columns: [
                { key: "group", header: "Gruppo" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "share_pct", header: "Quota %", type: "percent" },
              ],
            },
            {
              name: "Per tipo",
              rows: chartData.map((r) => ({
                type: r.type,
                occurrences: r.occurrences,
                planned: r.planned,
                actual: r.actual,
                variance: num(r.planned) - num(r.actual),
              })),
              columns: [
                { key: "type", header: "Tipo ricorrenza" },
                { key: "occurrences", header: "Scadenze", type: "integer" },
                { key: "planned", header: "Pianificato (€)", type: "currency" },
                { key: "actual", header: "Pagato (€)", type: "currency" },
                { key: "variance", header: "Δ (€)", type: "currency" },
              ],
            },
          ]}
          filenameBase={buildFilename(`ricorrenti_vs_una_tantum_${basis}`, {
            date_from: dateFrom,
            date_to: dateTo,
          })}
          testId="export-recurring"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          testId="recurring-planned"
          label="Spese ricorrenti — pianificato"
          value={num(data.recurring.planned)}
          tone="info"
        />
        <StatCard
          testId="onetime-planned"
          label="Una tantum — pianificato"
          value={num(data.one_time.planned)}
        />
        <StatCard
          testId="recurring-share"
          label="Quota ricorrenti / totale pagato"
          value={`${recShare.toFixed(1)}%`}
          isCurrency={false}
          tone="warning"
          hint="Maggiore quota ⇒ struttura di costo fissa"
        />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Ripartizione per tipo di ricorrenza
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%" minHeight={260}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="type" fontSize={11} stroke="#78716C" />
              <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="planned" name="Pianificato" fill="#2A4B3C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" name="Pagato" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Dettaglio per tipo
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo ricorrenza</TableHead>
              <TableHead className="text-right">Scadenze</TableHead>
              <TableHead className="text-right">Pianificato</TableHead>
              <TableHead className="text-right">Pagato</TableHead>
              <TableHead className="text-right">Δ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chartData.map((r) => (
              <TableRow key={r.type} data-testid={`recurring-row-${r.type}`}>
                <TableCell className="font-medium">{r.type}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{r.occurrences}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{EUR(r.planned)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{EUR(r.actual)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {EUR(num(r.planned) - num(r.actual))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}


/* ---------- Methods analysis (Cassa vs Banca operational tracking) ---------- */
function MethodsAnalysis({ dateFrom, dateTo }) {
  return (
    <section data-testid="section-methods" className="space-y-6">
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900">
        <strong>Pagamenti per metodo.</strong> Riepilogo operativo dei pagamenti
        registrati nel periodo, raggruppati per Contanti, Banca, POS e Altro.
        Serve a un controllo mensile veloce della distribuzione fra cassa e
        banca; non sostituisce la contabilità.
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PaymentMethodBreakdown
          dateFrom={dateFrom}
          dateTo={dateTo}
          title="Periodo selezionato"
          subtitle={`Periodo: ${dateFrom} → ${dateTo}`}
          testId="methods-period"
        />
        <PaymentMethodBreakdown
          dateFrom={(() => {
            const [from] = monthBounds();
            return from;
          })()}
          dateTo={(() => {
            const [, to] = monthBounds();
            return to;
          })()}
          title="Solo mese corrente"
          testId="methods-current-month"
        />
      </div>
    </section>
  );
}
